import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchControllerEvidenceCoverage,
  fetchControllerExceptionRate,
  fetchControllerFailingControls,
  fetchControllerIPEStatus,
  fetchControllerRollforward,
  fetchControllerSampleSize,
  fetchControllerSoDVerified,
  fetchControllerTestSummary,
  fetchControllerToDToE,
  type ControllerEvidenceCoverageRow,
  type ControllerExceptionRateRow,
  type ControllerFailingControlRow,
  type ControllerIPEStatusRow,
  type ControllerRollforwardRow,
  type ControllerSampleSizeRow,
  type ControllerSoDVerifiedRow,
  type ControllerTestSummaryRow,
  type ControllerToDToERow,
} from '../../request/analytics/controllerReports.ts';
import { analyticsControllerQueryKeys } from '../../queries/analytics/keys.ts';
import { useAnalyticsScope } from './useAnalyticsScope.tsx';
import { scopeKey } from '../../request/analytics/scope.ts';

/**
 * React Query v5 hooks for the Controller-persona analytics dashboard
 * (Feature 5.3, reports #20-#28). Each report is its own independent query
 * so a single failing/slow widget doesn't block the rest of the dashboard.
 */

export function useControllerTestSummary(): UseQueryResult<ControllerTestSummaryRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsControllerQueryKeys.testSummary(scopeKey(scope)),
    queryFn: () => fetchControllerTestSummary(scope),
  });
}

export function useControllerFailingControls(): UseQueryResult<ControllerFailingControlRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsControllerQueryKeys.failingControls(scopeKey(scope)),
    queryFn: () => fetchControllerFailingControls(scope),
  });
}

export function useControllerExceptionRate(): UseQueryResult<ControllerExceptionRateRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsControllerQueryKeys.exceptionRate(scopeKey(scope)),
    queryFn: () => fetchControllerExceptionRate(scope),
  });
}

export function useControllerSampleSize(): UseQueryResult<ControllerSampleSizeRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsControllerQueryKeys.sampleSize(scopeKey(scope)),
    queryFn: () => fetchControllerSampleSize(scope),
  });
}

export function useControllerRollforward(): UseQueryResult<ControllerRollforwardRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsControllerQueryKeys.rollforward(scopeKey(scope)),
    queryFn: () => fetchControllerRollforward(scope),
  });
}

export function useControllerIPEStatus(): UseQueryResult<ControllerIPEStatusRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsControllerQueryKeys.ipeStatus(scopeKey(scope)),
    queryFn: () => fetchControllerIPEStatus(scope),
  });
}

export function useControllerToDToE(): UseQueryResult<ControllerToDToERow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsControllerQueryKeys.todToe(scopeKey(scope)),
    queryFn: () => fetchControllerToDToE(scope),
  });
}

export function useControllerSoDVerified(): UseQueryResult<ControllerSoDVerifiedRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsControllerQueryKeys.sodVerified(scopeKey(scope)),
    queryFn: () => fetchControllerSoDVerified(scope),
  });
}

export function useControllerEvidenceCoverage(): UseQueryResult<ControllerEvidenceCoverageRow[], Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsControllerQueryKeys.evidenceCoverage(scopeKey(scope)),
    queryFn: () => fetchControllerEvidenceCoverage(scope),
  });
}
